import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Button, FormGroup } from "@cofano/cck";
import Grid from "@mui/material/Grid2";
import { intl } from "LocaleWrapper";
import { IconButton, Typography } from "@mui/material";
import FormField from "components/cck/FormField";
import Delete from "@mui/icons-material/Delete";
import Add from "@mui/icons-material/Add";
import NumberInput from "components/cck/NumberInput";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

export type FormValues = {
	agreements: {
		calculationMethod: {
			weightRanges?: {
				from?: number;
				to?: number;
				price?: number;
			}[];
		}
	}[];
}

type WeightRangesFormProps = {
	index: number;
	currency: CurrencyFragment;
}

const WeightRangesForm = ({ index, currency }: WeightRangesFormProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const { fields, append, remove } = useFieldArray({
		control,
		keyName: "generatedId",
		name: `agreements.${index}.calculationMethod.weightRanges`,
	});

	return (
		<Grid container spacing={2} size={12}>
			<Grid size={12} marginTop={2}>
				<Typography
					fontSize={13}
					fontWeight={"medium"}>
					{intl.formatMessage({ id: "RANGE" })}
				</Typography>
			</Grid>

			<Grid spacing={2}
				size={12}
				container
				sx={{ marginBottom: 1, alignItems: "center" }}>
				{fields.map((item, rangeIndex) => {
					return (
						<Grid container
							size={12}
							spacing={2}
							key={item.generatedId}>

							<Grid size={2}>
								<FormField
									errors={errors}
									name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.from`}
									min={0}
									max={10000}>
									<FormGroup label={intl.formatMessage({ id: "MTON_FROM" })}>
										<Controller
											name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.from`}
											control={control}
											rules={{ min: 1, max: 10000 }}
											render={({ field }) => <NumberInput {...field} />} />
									</FormGroup>
								</FormField>
							</Grid>

							<Grid size={2}>
								<FormField
									errors={errors}
									name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.to`}
									min={0}
									max={10000}>
									<FormGroup label={intl.formatMessage({ id: "MTON_TO" })}>
										<Controller
											name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.to`}
											control={control}
											rules={{ min: 1, max: 10000 }}
											render={({ field }) => <NumberInput {...field} />} />
									</FormGroup>
								</FormField>
							</Grid>

							<Grid size={3}>
								<FormField
									errors={errors}
									name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.price`}
									min={0}
									max={999999999}>
									<FormGroup label={intl.formatMessage({ id: "PRICE" })}>
										<Controller
											name={`agreements.${index}.calculationMethod.weightRanges.${rangeIndex}.price`}
											control={control}
											rules={{ required: true, min: 0, max: 999999999 }}
											render={({ field }) => (
												<NumberInput
													{...field}
													addonBefore={currency.symbol} />
											)} />
									</FormGroup>
								</FormField>
							</Grid>

							<Grid size={2}>
								<IconButton onClick={() => remove(rangeIndex)} size="small">
									<Delete fontSize="small" />
								</IconButton>
							</Grid>
						</Grid>
					);
				})}

				<Button
					icon={<Add fontSize="small" />}
					onClick={() => append({ from: 1, to: 10, price: 0 })}
					variant="outlined"
					size="small"
				/>
			</Grid>
		</Grid>
	);
};

export default WeightRangesForm;
